import {
  MESSAGE_ATTACHMENT_ACCEPT,
  MESSAGE_ATTACHMENT_NOTICES,
  formatFileSize,
  type MessageAttachmentLimits,
  type TicketMessageKind,
} from '@easyhelp/shared';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useRef, useState, type ChangeEvent, type DragEvent } from 'react';
import { attachmentProblems, sameFile } from './attachment-checks';

interface ComposerAttachmentsProps {
  kind: TicketMessageKind;
  files: readonly File[];
  onChange: (files: File[]) => void;
  limits: MessageAttachmentLimits;
  disabled?: boolean;
}

/**
 * Allegati del composer (story #43, DEC-008): «Allega» o trascinamento sull'area, elenco con nome,
 * dimensione e rimozione, verifica dei limiti dell'istanza prima dell'invio. L'avviso cambia con la
 * modalità: sulla risposta il file parte con l'email, sulla nota resta interno.
 */
export function ComposerAttachments({ kind, files, onChange, limits, disabled = false }: ComposerAttachmentsProps) {
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const problems = attachmentProblems(files, limits);

  function add(picked: Iterable<File>) {
    const next = [...files];
    for (const file of picked) if (!next.some((existing) => sameFile(existing, file))) next.push(file);
    onChange(next);
  }

  function pick(event: ChangeEvent<HTMLInputElement>) {
    if (event.target.files) add(event.target.files);
    // Lo stesso file, tolto e riscelto, deve scatenare di nuovo `change`.
    event.target.value = '';
  }

  function drop(event: DragEvent) {
    event.preventDefault();
    setDragging(false);
    if (!disabled) add(event.dataTransfer.files);
  }

  return (
    <Stack
      spacing={2}
      data-testid="composer-attachments"
      onDragOver={(event) => {
        event.preventDefault();
        if (!disabled) setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={drop}
      sx={{
        p: 2,
        border: 1,
        borderStyle: 'dashed',
        borderRadius: 2,
        borderColor: dragging ? 'primary.main' : 'divider',
        bgcolor: dragging ? 'primary.lighter' : 'transparent',
      }}
    >
      <Stack direction="row" spacing={2} alignItems="center" useFlexGap sx={{ flexWrap: 'wrap' }}>
        <Button
          size="small"
          variant="outlined"
          startIcon={<AttachFileIcon />}
          disabled={disabled}
          onClick={() => inputRef.current?.click()}
        >
          Allega
        </Button>
        {/* Nascosto: lo apre «Allega», un pulsante vero raggiungibile e attivabile da tastiera. */}
        <input
          ref={inputRef}
          hidden
          type="file"
          multiple
          accept={MESSAGE_ATTACHMENT_ACCEPT}
          onChange={pick}
          aria-label="Scegli i file da allegare"
          tabIndex={-1}
        />
        <Typography variant="body2" color="text.secondary">
          oppure trascina qui i file · immagini, PDF e testo, fino a {formatFileSize(limits.maxFileBytes)} l’uno e{' '}
          {formatFileSize(limits.maxMessageBytes)} in tutto
        </Typography>
      </Stack>

      <Stack direction="row" spacing={1} alignItems="center">
        {kind === 'risposta' ? (
          <WarningAmberOutlinedIcon fontSize="small" color="warning" />
        ) : (
          <LockOutlinedIcon fontSize="small" color="action" />
        )}
        <Typography variant="body2" data-testid="attachment-notice">
          {MESSAGE_ATTACHMENT_NOTICES[kind]}
        </Typography>
      </Stack>

      {files.length > 0 && (
        <Box component="ul" aria-label="Allegati scelti" sx={{ m: 0, p: 0, listStyle: 'none' }}>
          {files.map((file, index) => (
            <Stack
              component="li"
              key={`${file.name}:${file.size}:${file.lastModified}`}
              direction="row"
              spacing={1}
              alignItems="center"
            >
              <InsertDriveFileOutlinedIcon fontSize="small" color="action" />
              <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                {file.name}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                {formatFileSize(file.size)}
              </Typography>
              <IconButton
                size="small"
                aria-label={`Rimuovi ${file.name}`}
                disabled={disabled}
                onClick={() => onChange(files.filter((_, i) => i !== index))}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Stack>
          ))}
        </Box>
      )}

      {problems.length > 0 && (
        <Alert severity="error">
          {problems.map((problem) => (
            <div key={problem}>{problem}</div>
          ))}
        </Alert>
      )}
    </Stack>
  );
}
